import { useCallback, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MAX_VAULT_DOCUMENT_BYTES,
  type ListVaultDocumentsQuery,
  type UpdateVaultDocumentInput,
  type VaultDateGroup,
  type VaultDocument,
} from '@accounting/shared';
import { ApiError, api } from '../lib/api';

export type VaultFilters = Partial<
  Pick<ListVaultDocumentsQuery, 'q' | 'docType' | 'from' | 'to' | 'needsReview' | 'folder'>
>;

export const vaultKey = (companyId: string | null, filters: VaultFilters) =>
  ['vault-documents', companyId ?? 'none', filters] as const;

interface VaultListResponse {
  documents: VaultDocument[];
  groups: VaultDateGroup[];
}

export function useVaultDocuments(companyId: string | null, filters: VaultFilters) {
  return useQuery({
    queryKey: vaultKey(companyId, filters),
    enabled: companyId !== null,
    queryFn: () => {
      const params: Record<string, string> = {};
      if (filters.q) params.q = filters.q;
      if (filters.docType) params.docType = filters.docType;
      if (filters.from) params.from = filters.from;
      if (filters.to) params.to = filters.to;
      if (filters.needsReview) params.needsReview = 'true';
      // Omitted = loose documents only. Send it explicitly to open a named
      // folder, or '*' to browse everything at once.
      if (filters.folder) params.folder = filters.folder;
      return api.get<VaultListResponse>(`/companies/${companyId}/vault/documents`, params);
    },
  });
}

export function useUpdateVaultDocument(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateVaultDocumentInput }) => {
      if (!companyId) throw new Error('No active company');
      return api
        .patch<{ document: VaultDocument }>(`/companies/${companyId}/vault/documents/${id}`, input)
        .then((r) => r.document);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vault-documents', companyId ?? 'none'] }),
  });
}

export function useDeleteVaultDocument(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => {
      if (!companyId) throw new Error('No active company');
      return api.delete<void>(`/companies/${companyId}/vault/documents/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vault-documents', companyId ?? 'none'] }),
  });
}

/** Re-run the AI read on a document that failed or was filed wrong. */
export function useRetryExtraction(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => {
      if (!companyId) throw new Error('No active company');
      return api
        .post<{ document: VaultDocument }>(`/companies/${companyId}/vault/documents/${id}/extract`, {})
        .then((r) => r.document);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vault-documents', companyId ?? 'none'] }),
  });
}

// ── Bulk upload ────────────────────────────────────────────────────────────

export type UploadPhase = 'queued' | 'uploading' | 'reading' | 'done' | 'error';

export interface UploadItem {
  key: string;
  fileName: string;
  sizeBytes: number;
  phase: UploadPhase;
  /** Set once the bytes are safely stored, even if the AI read then fails. */
  documentId?: string;
  /** Populated on a hard failure (the file was NOT stored). */
  error?: string;
  /** Populated when the file stored fine but could not be read automatically. */
  warning?: string;
}

/** How many files are in flight at once. Keeps a 40-file drop civil. */
const UPLOAD_CONCURRENCY = 2;

function apiMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string; message?: string } | null;
    return body?.error ?? body?.message ?? `Upload failed (${err.status})`;
  }
  return err instanceof Error ? err.message : 'Upload failed';
}

/** Read a File as base64 without the `data:` URL prefix. */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('Could not read the file'));
    reader.readAsDataURL(file);
  });
}

/**
 * Drop a stack of files. Each one is uploaded (bytes committed server-side),
 * then read by the AI as a separate call — so a file whose date cannot be read
 * still lands in the vault, and one bad file never stops the rest of the batch.
 */
export function useVaultUpload(companyId: string | null) {
  const qc = useQueryClient();
  const [items, setItems] = useState<UploadItem[]>([]);
  const [busy, setBusy] = useState(false);
  const seq = useRef(0);

  const patch = useCallback((key: string, next: Partial<UploadItem>) => {
    setItems((prev) => prev.map((it) => (it.key === key ? { ...it, ...next } : it)));
  }, []);

  const upload = useCallback(
    async (files: File[]) => {
      if (!companyId || files.length === 0) return;

      const queued: { item: UploadItem; file: File }[] = files.map((file) => ({
        file,
        item: {
          key: `u${seq.current++}`,
          fileName: file.name,
          sizeBytes: file.size,
          phase: 'queued' as const,
        },
      }));
      setItems((prev) => [...queued.map((q) => q.item), ...prev]);
      setBusy(true);

      let cursor = 0;
      const worker = async (): Promise<void> => {
        for (;;) {
          const next = queued[cursor++];
          if (!next) return;
          const { item, file } = next;

          if (file.size > MAX_VAULT_DOCUMENT_BYTES) {
            patch(item.key, {
              phase: 'error',
              error: `Too large — the limit is ${MAX_VAULT_DOCUMENT_BYTES / 1024 / 1024} MB.`,
            });
            continue;
          }

          patch(item.key, { phase: 'uploading' });
          let stored: VaultDocument;
          try {
            const content = await fileToBase64(file);
            const res = await api.post<{ document: VaultDocument }>(
              `/companies/${companyId}/vault/documents`,
              {
                fileName: file.name,
                mimeType: file.type || 'application/octet-stream',
                content,
              },
            );
            stored = res.document;
          } catch (err) {
            patch(item.key, { phase: 'error', error: apiMessage(err) });
            continue;
          }

          // The bytes are safe from here on. Anything the AI does next can only
          // add metadata; the document is already in the vault.
          patch(item.key, { phase: 'reading', documentId: stored.id });
          try {
            const res = await api.post<{ document: VaultDocument }>(
              `/companies/${companyId}/vault/documents/${stored.id}/extract`,
              {},
            );
            patch(item.key, {
              phase: 'done',
              warning:
                res.document.extractionStatus === 'done'
                  ? undefined
                  : res.document.extractionError ?? 'Filed under its upload month — needs your review.',
            });
          } catch (err) {
            patch(item.key, {
              phase: 'done',
              warning: `Stored, but could not be read automatically: ${apiMessage(err)}`,
            });
          }
        }
      };

      await Promise.all(
        Array.from({ length: Math.min(UPLOAD_CONCURRENCY, queued.length) }, () => worker()),
      );

      setBusy(false);
      void qc.invalidateQueries({ queryKey: ['vault-documents', companyId] });
    },
    [companyId, patch, qc],
  );

  const clearFinished = useCallback(() => {
    setItems((prev) => prev.filter((it) => it.phase !== 'done' && it.phase !== 'error'));
  }, []);

  return { items, busy, upload, clearFinished };
}
